import { ReactNode, useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { LinkLogo } from '@/components/ui/LinkLogo';
import { Button } from '@/components/ui/button';
import { 
  LogOut, 
  ClipboardCheck, 
  BookOpen, 
  Plug, 
  User,
  Menu,
  FileText,
  Package,
  Rocket,
  LayoutDashboard,
  Server,
  PackageSearch,
  Ghost,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DemoPathToggle } from '@/components/ui/DemoPathToggle';
import { toast } from 'sonner';

interface OpsLayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/ops', label: 'Queue', icon: ClipboardCheck, end: true },
  { path: '/ops/rules', label: 'Rules Engine', icon: BookOpen },
  { path: '/admin/payer-rules', label: 'Payer Rules', icon: FileText },
  { path: '/admin/procedure-catalog', label: 'Procedures', icon: Package },
  { path: '/admin/procedure-bundles', label: 'Bundles', icon: Package },
  { path: '/admin/bulk-import', label: 'Import', icon: Upload },
  { path: '/ops/integrations', label: 'Integrations', icon: Plug },
  { path: '/admin/orchestrator', label: 'Orchestrator', icon: LayoutDashboard },
  { path: '/admin/integration-sandbox', label: 'Sandbox', icon: Server },
  { path: '/onboarding-hub', label: 'Onboarding Hub', icon: Rocket },
  { path: '/ops/supply-sync', label: 'Supply Sync', icon: PackageSearch },
  { path: '/ops/ghost-supply-roi', label: 'Ghost Supply', icon: Ghost },
];

export function OpsLayout({ children }: OpsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, profile } = useAuthContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    toast.success('Securely Signed Out', {
      description: 'Your session has been terminated and all cached data cleared.',
    });
  };

  const NavItems = () => (
    <>
      {navItems.map(({ path, label, icon: Icon, end }) => {
        const isActive = end 
          ? location.pathname === path 
          : location.pathname.startsWith(path);
        return (
          <NavLink
            key={path}
            to={path}
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center justify-between h-14 px-4 lg:px-6">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <LinkLogo size="sm" />
              <span className="text-xs font-medium text-muted-foreground hidden sm:inline">OPS</span>
            </button>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <NavItems />
            </nav>
          </div>

          {/* Right: User + Actions */}
          <div className="flex items-center gap-3">
            {/* Demo Path Toggle (only in demo mode) */}
            <DemoPathToggle />
            
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="max-w-[150px] truncate">{profile?.full_name || 'User'}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Sign Out</span>
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64">
                <div className="flex flex-col gap-4 mt-6">
                  <NavItems />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">{children}</div>
      </main>
    </div>
  );
}
